import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type Dispatch,
	type SetStateAction,
} from 'react';

import { formatCurrency } from '@liquidweb-dev/banana-stand/format';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import toast from '@liquidweb-dev/undercurrent/toast';

import FadeCollapse from 'components/FadeCollapse';
import { AcceptDispatchOkI } from 'modules/acceptJs';
import {
	ConfirmSavedPaymentContent,
	ConfirmNewAuthNetPaymentContent,
	ConfirmNewStripePaymentContent,
	StartContent,
} from './contents';
import dialogMakePaymentContext, {
	type DialogMakePaymentFormErrors,
	type DialogMakePaymentFormValues,
	type HeldStripePaymentI,
} from './dialogMakePaymentContext';
import StripeProvider from './StripeProvider';
import titles, { type DialogMakePaymentStep } from './titles';
import { type ResolvedPaymentMethodI } from './usePaymentMethods';

const initialFormValues: DialogMakePaymentFormValues = {
	amount: '0',
	payFullBalance: false,
	cardCode: '',
	cardNumber: '',
	expirationDate: '',
	savePaymentMethod: false,
	paymentMode: 'saved',
	paymentInstrumentId: '',
};

const initialFormErrors: DialogMakePaymentFormErrors = {
	amount: '',
	payFullBalance: '',
	cardCode: '',
	cardNumber: '',
	expirationDate: '',
	savePaymentMethod: '',
	paymentMode: '',
	paymentInstrumentId: '',
};

/** The values the form opens with, and the ones it returns to when it closes */
const getInitialFormValues = ({
	accountBalanceDisplay,
	defaultPaymentInstrumentId,
	initialAmount,
	initialPaymentMode,
}: {
	accountBalanceDisplay: string;
	defaultPaymentInstrumentId: string;
	initialAmount?: string;
	initialPaymentMode: DialogMakePaymentFormValues['paymentMode'];
}): DialogMakePaymentFormValues => {
	const amount = initialAmount ?? accountBalanceDisplay;

	return {
		...initialFormValues,
		amount,
		/**
		 * True only when the opening amount is the full balance and there is one to pay:
		 * the switch locks the field to the balance, so a different supplied amount has to
		 * open editable.
		 */
		payFullBalance:
			Number(accountBalanceDisplay) > 0 &&
			Number(amount) === Number(accountBalanceDisplay),
		paymentMode: initialPaymentMode,
		paymentInstrumentId: defaultPaymentInstrumentId,
	};
};

export interface PaymentStepsProps {
	accountBalanceDisplay: string;
	defaultPaymentInstrumentId: string;
	hasPaymentMethodsError: boolean;
	heldStripePayment: HeldStripePaymentI | null;
	/** Opens the amount field with this instead of the full balance. */
	initialAmount?: string;
	/** false while a Stripe payment is in flight or its card is held uncaptured */
	isClosable: boolean;
	isPayPalAllowed: boolean;
	isStripeAllowed: boolean;
	/** whether the dialog holding these steps is open */
	open: boolean;
	paymentMethods: ResolvedPaymentMethodI[];
	onClose: () => void;
	refetchPaymentMethods: () => void;
	setHeldStripePayment: Dispatch<SetStateAction<HeldStripePaymentI | null>>;
}

/**
 * The steps of the payment, and the form state they share. Mounted only once the
 * account data has arrived, so every value it opens with is ready to be an
 * initial value - nothing to initialize later, and nothing to rearrange under
 * the user.
 */
const PaymentSteps = ({
	accountBalanceDisplay,
	defaultPaymentInstrumentId,
	hasPaymentMethodsError,
	heldStripePayment,
	initialAmount,
	isClosable,
	isPayPalAllowed,
	isStripeAllowed,
	open,
	paymentMethods,
	onClose,
	refetchPaymentMethods,
	setHeldStripePayment,
}: PaymentStepsProps) => {
	// an account with nothing on file to pay with starts on a new card - a failure
	// to fetch is not an empty wallet, and has its own say on the saved panel
	const initialPaymentMode =
		paymentMethods.length > 0 || hasPaymentMethodsError ? 'saved' : 'new';

	const [step, setStep] = useState<DialogMakePaymentStep>('start');
	const [opaqueData, setOpaqueData] = useState<
		AcceptDispatchOkI['opaqueData'] | null
	>(null);
	const [formValues, setFormValues] = useState<DialogMakePaymentFormValues>(
		() =>
			getInitialFormValues({
				accountBalanceDisplay,
				defaultPaymentInstrumentId,
				initialAmount,
				initialPaymentMode,
			}),
	);
	const [formErrors, setFormErrors] = useState(initialFormErrors);

	const [confirmationToken, setConfirmationToken] = useState<string | null>(
		null,
	);

	/** Puts the steps back to how the dialog opens. */
	const reset = useCallback(() => {
		setStep('start');
		setOpaqueData(null);
		setConfirmationToken(null);
		setFormErrors(initialFormErrors);
		setFormValues(
			getInitialFormValues({
				accountBalanceDisplay,
				defaultPaymentInstrumentId,
				initialAmount,
				initialPaymentMode,
			}),
		);
	}, [
		accountBalanceDisplay,
		defaultPaymentInstrumentId,
		initialAmount,
		initialPaymentMode,
	]);

	/** The one way out of the dialog, so no close of ours can skip the reset. */
	const handleClose = useCallback(() => {
		// checked before the reset, which would wipe a payment that is being taken
		if (!isClosable) return;

		reset();
		onClose();
	}, [isClosable, reset, onClose]);

	// The backstop for the closes that are not ours: escape and a backdrop click
	// are the `Dialog`'s own, wired above these steps, so they never reach
	// `handleClose`. Resetting again after one of ours costs nothing.
	useEffect(() => {
		if (!open) {
			reset();
		}
	}, [open, reset]);

	/**
	 * Below `sm` the dialog is a drawer that stays mounted, so these steps only read
	 * `initialAmount` once. Reseeding on each closed-to-open edge lets every opening take
	 * its own amount. Only the edge reseeds: `reset` changes when the balance refetches,
	 * which must not wipe a form being filled in. A layout effect, so the stale amount is
	 * never painted.
	 */
	const wasOpen = useRef(open);
	useLayoutEffect(() => {
		if (open && !wasOpen.current) {
			reset();
		}
		wasOpen.current = open;
	}, [open, reset]);

	const clearErrors = () => setFormErrors(initialFormErrors);

	const clearSensitiveFormValues = useCallback(() => {
		setFormValues((prev) => ({
			...prev,
			cardCode: '',
			cardNumber: '',
			expirationDate: '',
		}));
		// also clear any errors associated with these form values
		setFormErrors((prev) => ({
			...prev,
			cardCode: '',
			cardNumber: '',
			expirationDate: '',
		}));
	}, []);

	const handleStepChange = (newStep: DialogMakePaymentStep) => {
		setStep(newStep);
		clearErrors();
	};

	const handleSubmitSuccess = () => {
		// a payment that went through holds nothing, so this close skips the guard
		reset();
		onClose();
		toast.success('Payment successful', {
			description: `Your payment of ${formatCurrency(formValues.amount)} was confirmed`,
		});
	};

	/**
	 * An error during the final submit is deliberately vague since we are not certain if the payment was in fact captured during an error.
	 */
	const handleSubmitError = () => {
		toast.danger('We couldn’t complete your payment', {
			description:
				'Check your balance before trying again, and contact support if it keeps failing.',
		});
		setStep('start');
	};

	return (
		<>
			<DialogTitle onClose={handleClose}>{titles[step]}</DialogTitle>
			<DialogContent>
				<dialogMakePaymentContext.Provider
					value={{
						accountBalanceDisplay,
						confirmationToken,
						formErrors,
						formValues,
						hasPaymentMethodsError,
						heldStripePayment,
						isPayPalAllowed,
						isStripeAllowed,
						paymentMethods,
						opaqueData,
						step,
						clearSensitiveFormValues,
						onSubmitSuccess: handleSubmitSuccess,
						onSubmitError: handleSubmitError,
						refetchPaymentMethods,
						setConfirmationToken,
						setFormErrors,
						setHeldStripePayment,
						setFormValues,
						setOpaqueData,
						setStep: handleStepChange,
					}}
				>
					<StripeProvider>
						{/* each step is unmounted while collapsed - a step that isn't on
						screen shouldn't still be holding form inputs or firing requests */}
						<FadeCollapse in={step === 'start'} unmountOnExit>
							<StartContent />
						</FadeCollapse>

						<FadeCollapse in={step === 'confirmSavedPayment'} unmountOnExit>
							<ConfirmSavedPaymentContent />
						</FadeCollapse>

						<FadeCollapse
							in={step === 'confirmNewAuthNetPayment'}
							unmountOnExit
						>
							<ConfirmNewAuthNetPaymentContent />
						</FadeCollapse>

						<FadeCollapse in={step === 'confirmNewStripePayment'} unmountOnExit>
							<ConfirmNewStripePaymentContent />
						</FadeCollapse>
					</StripeProvider>
				</dialogMakePaymentContext.Provider>
			</DialogContent>
		</>
	);
};

export default PaymentSteps;
